import React, { useState, useEffect, useLayoutEffect, Suspense } from 'react';
import './App.css';
import { unstable_HistoryRouter as HistoryRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { ComponentLoading } from './components/SimpleLoading/SimpleLoading';
import { shouldShowOnboardingV2 } from './components/AppTour/onboardingStorage';
import { createPortal } from 'react-dom';
import { applySeo, getRouteSeo } from './seo/seo';
import { qaPostPath } from './seo/qaPosts';
import { initAOS } from './utils/aos';
import { createTransitionHistory, notifyRouteCommitted } from './utils/pageTransition';
import { tabPageImports, preloadAllTabPages } from './utils/tabPages';
import useIsDesktop from './hooks/useIsDesktop';
// Màn hình tải hiện ngay lần render đầu nên để trong bundle chính (không chờ tải thêm chunk mới vẽ được)
import AppLoading from './components/AppLoading/AppLoading';
// Bọc mọi trang: rất nhỏ nên để trong bundle chính, tránh thêm một vòng tải chunk trước khi tải được trang
import FastPageTransition from './components/FastPageTransition/FastPageTransition';
import DesktopFooter from './components/DesktopFooter/DesktopFooter';

// Lazy load components để giảm bundle size
const PageTransition = React.lazy(() => import('./components/PageTransition/PageTransition'));
const BottomNavigation = React.lazy(() => import('./components/BottomNavigation/BottomNavigation'));
const TopBar = React.lazy(() => import('./components/TopBar/TopBar'));
// Tour nặng (chỉ mở khi cần) nên tách khỏi bundle chính
const AppTourV2 = React.lazy(() => import('./components/AppTour/AppTourV2'));
const GlobalNotification = React.lazy(() => import('./components/GlobalNotification/GlobalNotification'));

// Lazy imports for pages - tối ưu hóa thêm
const Home = React.lazy(tabPageImports['/']);
const Login = React.lazy(() => import('./pages/Auth/Login'));
const AuthStatus = React.lazy(() => import('./pages/Auth/AuthStatus'));
const GoogleRedirect = React.lazy(() => import('./pages/Auth/GoogleRedirect'));
const GoogleCallback = React.lazy(() => import('./pages/Auth/GoogleCallback'));
const FacebookRedirect = React.lazy(() => import('./pages/Auth/FacebookRedirect'));
const FacebookCallback = React.lazy(() => import('./pages/Auth/FacebookCallback'));
const Orders = React.lazy(tabPageImports['/orders']);
const ShopeeFood = React.lazy(() => import('./pages/ShopeeFood/ShopeeFood'));
const Payments = React.lazy(tabPageImports['/payments']);
const PaymentSetup = React.lazy(() => import('./pages/PaymentSetup/PaymentSetup'));
const Account = React.lazy(tabPageImports['/account']);
const LostOrderSearchPage = React.lazy(() => import('./pages/LostOrderSearch/LostOrderSearch'));
const QA = React.lazy(() => import('./pages/QA/QA'));
const QADetail = React.lazy(() => import('./pages/QA/QADetail'));
const EditPost = React.lazy(() => import('./pages/QA/EditPost'));
const AdminPanel = React.lazy(() => import('./pages/QA/AdminPanel'));
const BannersAdmin = React.lazy(() => import('./pages/Admin/BannersAdmin'));
const VouchersPage = React.lazy(() => import('./pages/Vouchers/Vouchers'));
const CouponsPage = React.lazy(tabPageImports['/ma-giam-gia']);
const ShopVoucher0dPage = React.lazy(() => import('./pages/Coupons/ShopVoucher0d'));
const VouchersSale88Page = React.lazy(() => import('./pages/Vouchers/VouchersSale88'));
const VouchersSale158Page = React.lazy(() => import('./pages/Vouchers/VouchersSale158'));
const VouchersSale1010Page = React.lazy(() => import('./pages/Vouchers/VouchersSale1010'));
const VouchersSale1510Page = React.lazy(() => import('./pages/Vouchers/VouchersSale1510'));
const VouchersSale2510Page = React.lazy(() => import('./pages/Vouchers/VouchersSale2510'));
const VouchersSale1111Page = React.lazy(() => import('./pages/Vouchers/VouchersSale1111'));
const VouchersSale1212Page = React.lazy(() => import('./pages/Vouchers/VouchersSale1212'));
const FlashSalePage = React.lazy(tabPageImports['/flashsale']);
const About = React.lazy(() => import('./pages/About/About'));
const NotFound = React.lazy(() => import('./components/NotFound/NotFound'));

// Component wrapper để check demo order và cho phép truy cập Orders khi chưa đăng nhập
const OrdersWrapper = ({ children }) => {
  // Initialize state từ localStorage ngay lập tức để tránh redirect
  const [hasDemoOrder, setHasDemoOrder] = React.useState(() => {
    if (typeof window !== 'undefined') {
      try {
        return localStorage.getItem('demo_order_from_tour') === 'true';
      } catch (error) {
        return false;
      }
    }
    return false;
  });
  
  // State để đợi check flag hoàn tất trước khi redirect
  const [isChecking, setIsChecking] = React.useState(true);
  
  // Get user from AuthContext - AuthProvider is available in index.js
  const { user } = useAuth();
  
  React.useEffect(() => {
    // Chỉ chạy trên client-side
    if (typeof window === 'undefined') {
      setIsChecking(false);
      return;
    }
    
    // Check localStorage khi component mount
    const checkDemoOrder = () => {
      try {
        const demoFlag = localStorage.getItem('demo_order_from_tour') === 'true';
        setHasDemoOrder(demoFlag);
        setIsChecking(false); // Đánh dấu đã check xong
      } catch (error) {
        // localStorage có thể không khả dụng
        console.error('Error checking demo order:', error);
        setIsChecking(false);
      }
    };
    
    // Check ngay lập tức
    checkDemoOrder();
    
    // Listen for demo order injection
    const handleDemoOrderInjected = () => {
      // Đợi một chút để đảm bảo localStorage đã được set
      setTimeout(checkDemoOrder, 100);
    };
    
    // Listen for storage changes (khi localStorage được set từ component khác)
    const handleStorageChange = (e) => {
      if (e.key === 'demo_order_from_tour') {
        checkDemoOrder();
      }
    };
    
    try {
      window.addEventListener('demoOrderInjected', handleDemoOrderInjected);
      window.addEventListener('storage', handleStorageChange);
      
      // Đợi tối đa 2s để check flag, sau đó mới redirect (tăng timeout)
      const timeout = setTimeout(() => {
        setIsChecking(false);
      }, 2000);
      
      return () => {
        try {
          window.removeEventListener('demoOrderInjected', handleDemoOrderInjected);
          window.removeEventListener('storage', handleStorageChange);
          clearTimeout(timeout);
        } catch (error) {
          // Ignore cleanup errors
        }
      };
    } catch (error) {
      console.error('Error setting up demo order listener:', error);
      setIsChecking(false);
    }
  }, []);
  
  // Cho phép truy cập nếu có user hoặc có demo order
  // Kiểm tra lại flag trực tiếp từ localStorage để đảm bảo không bỏ sót
  const currentDemoFlag = typeof window !== 'undefined' 
    ? localStorage.getItem('demo_order_from_tour') === 'true' 
    : false;
  const finalHasDemoOrder = hasDemoOrder || currentDemoFlag;
  
  if (user || finalHasDemoOrder) {
    return children;
  }
  
  // Đợi check flag xong trước khi redirect
  // Kiểm tra flag nhiều lần để đảm bảo bắt được flag ngay cả khi set sau khi component mount
  if (isChecking || currentDemoFlag) {
    // Nếu có flag, cho phép truy cập ngay
    if (currentDemoFlag) {
      return children;
    }
    // Render null hoặc loading indicator trong lúc chờ
    return null;
  }
  
  // Chỉ redirect sau khi đã check flag xong và không có flag
  // Kiểm tra lại flag một lần nữa trước khi redirect
  const lastCheck = typeof window !== 'undefined' 
    ? localStorage.getItem('demo_order_from_tour') === 'true' 
    : false;
  if (lastCheck) {
    return children;
  }
  
  return <Navigate to="/login" replace />;
};

// URL hỏi đáp cũ /qa/posts/:id -> /hoi-dap/<slug> (trên Cloudflare đã có 301 trong _redirects cho bài đã biết slug,
// route này lo phần còn lại khi chạy client). Bài chưa có slug đặt tay thì QADetail đổi sang URL chuẩn sau khi tải bài
const LegacyQaPostRedirect = () => {
  const { id } = useParams();
  const location = useLocation();
  return <Navigate to={`${qaPostPath(id)}${location.hash}`} replace />;
};

// Component wrapper để thêm TopBar và BottomNavigation - tối ưu hóa
const AppContent = ({ children, showBottomNav = true }) => {
  const location = useLocation();
  
  // useLayoutEffect chạy trước useEffect của trang con, nên trang con (QADetail, NotFound) có thể ghi đè SEO mặc định của route
  useLayoutEffect(() => {
    applySeo(getRouteSeo(location.pathname));
  }, [location.pathname]);

  // Memoize các giá trị để tránh re-render không cần thiết
  const activeTab = React.useMemo(() => {
    if (location.pathname === '/') return 'home';
    if (location.pathname === '/orders') return 'orders';
    if (location.pathname === '/shopeefood') return 'shopeefood';
    if (location.pathname === '/payments' || location.pathname === '/payment-setup') return 'payments';
    if (location.pathname === '/hoi-dap' || location.pathname.startsWith('/hoi-dap/') || location.pathname.startsWith('/qa/')) return 'qa';
    if (location.pathname === '/account') return 'account';
    if (location.pathname === '/flashsale') return 'flashsale';
    return 'home';
  }, [location.pathname]);

  const topBarTitle = React.useMemo(() => {
    if (location.pathname === '/') return 'CaskBack';
    if (location.pathname === '/vouchers' || location.pathname === '/ma-giam-gia') return 'Mã giảm giá';
    if (location.pathname === '/ma-shop-0d') return 'Mã shop 0đ';
    if (location.pathname === '/ma-giam-gia-8-8' || location.pathname === '/vouchers/sale-8-8') return 'Mã Shop 0đ Sale 8.8';
    if (location.pathname === '/ma-giam-gia-15-8' || location.pathname === '/vouchers/sale-15-8') return 'Mã Shop 0đ Sale 15.8';
    if (location.pathname === '/ma-giam-gia-10-10' || location.pathname === '/vouchers/sale-10-10') return 'Mã Shop 0đ Sale 10.10';
    if (location.pathname === '/ma-giam-gia-15-10' || location.pathname === '/vouchers/sale-15-10') return 'Mã Shop 0đ Sale 15.10';
    if (location.pathname === '/ma-giam-gia-25-10' || location.pathname === '/vouchers/sale-25-10') return 'Mã Shop 0đ Sale 25.10';
    if (location.pathname === '/ma-giam-gia-11-11' || location.pathname === '/vouchers/sale-11-11') return 'Mã Shop 0đ Sale 11.11';
    if (location.pathname === '/ma-giam-gia-12-12' || location.pathname === '/vouchers/sale-12-12') return 'Mã Shop 0đ Sale 12.12';
    if (location.pathname === '/orders') return 'Quản lý đơn hàng';
    if (location.pathname === '/shopeefood') return 'ShopeeFood';
    if (location.pathname === '/flashsale') return 'Flash Sale';
    if (location.pathname === '/payments') return 'Thanh toán';
    if (location.pathname === '/payment-setup') return 'Thiết lập thanh toán';
    if (location.pathname === '/hoi-dap' || location.pathname.startsWith('/hoi-dap/')) return 'Hỏi Đáp';
    if (location.pathname === '/qa/admin') return 'Quản Lý Bài Viết';
    if (location.pathname.includes('/edit')) return 'Chỉnh sửa bài viết';
    if (location.pathname.startsWith('/qa/')) return 'Hỏi Đáp';
    if (location.pathname === '/account') return 'Tài khoản';
    if (location.pathname === '/lost-order-search') return 'Tìm đơn thất lạc';
    if (location.pathname === '/login') return 'CaskBack';
    return 'CaskBack';
  }, [location.pathname]);

  const isDesktop = useIsDesktop();

  const shouldShowTopBar = React.useMemo(() => {
    // Flash Sale có đầu trang riêng (tiêu đề + tìm kiếm + yêu thích) nên trên mobile không cần TopBar,
    // nhưng trên PC vẫn cần vì menu điều hướng nằm ở đó
    return !location.pathname.startsWith('/auth/status') &&
           !location.pathname.startsWith('/auth/logout') &&
           location.pathname !== '/login' &&
           (location.pathname !== '/flashsale' || isDesktop);
  }, [location.pathname, isDesktop]);

  // Trang mã giảm giá shop (sale theo ngày): ẩn bottom nav, chỉ có nút back trên TopBar
  const isShopVoucherPage = /^\/(ma-giam-gia-|vouchers\/sale-)/.test(location.pathname);

  const shouldShowNotification = React.useMemo(() => {
    return location.pathname === '/';
  }, [location.pathname]);

  const shouldShowBottomNav = React.useMemo(() => {
    return showBottomNav && !isShopVoucherPage && !location.pathname.startsWith('/hoi-dap/') && !location.pathname.startsWith('/qa/posts/');
  }, [showBottomNav, isShopVoucherPage, location.pathname]);

  // .App nằm ngoài AppContent nên đánh dấu lên body để CSS bỏ khoảng chừa cho bottom nav
  useEffect(() => {
    document.body.classList.toggle('no-bottom-nav', !shouldShowBottomNav);
    return () => document.body.classList.remove('no-bottom-nav');
  }, [shouldShowBottomNav]);

  const isAuthPage = React.useMemo(() => {
    return location.pathname === '/login' || location.pathname.startsWith('/auth/');
  }, [location.pathname]);

  return (
    <div className={isAuthPage ? 'auth-page' : ''}>
      {/* TopBar với Suspense */}
      {shouldShowTopBar && (
        <Suspense fallback={<div className="topbar-placeholder" style={{height: '56px'}} />}>
          <TopBar title={topBarTitle} showNotification={shouldShowNotification} showBackButton={isShopVoucherPage} />
        </Suspense>
      )}
      {/* Vùng nội dung chính (landmark main) cho trình đọc màn hình, nằm giữa TopBar và BottomNav */}
      <main>{children}</main>
      {/* Chân trang chỉ dành cho PC (CSS ẩn dưới 1024px); trang đăng nhập/xác thực không có */}
      {!isAuthPage && <DesktopFooter />}
      {/* BottomNav với Suspense */}
      {shouldShowBottomNav && (
        <Suspense fallback={<div className="bottomnav-placeholder" style={{height: '65px'}} />}>
          <BottomNavigation activeTab={activeTab} />
        </Suspense>
      )}
    </div>
  );
};

// History chung cho Router: mọi lần đổi trang chạy qua View Transitions (utils/pageTransition.js)
const appHistory = createTransitionHistory();

// Báo route mới đã commit để hiệu ứng chuyển trang chụp trang mới (cả lúc trang chờ tải chunk)
const RouteCommitSignal = () => {
  const { pathname } = useLocation();
  useLayoutEffect(() => {
    notifyRouteCommitted();
  }, [pathname]);
  return null;
};

// Component để xử lý tour logic bên trong Router
const TourHandler = ({ loading, onShowTour }) => {
  const location = useLocation();
  
  // Check if onboarding should be shown (cho phép cả khi chưa đăng nhập)
  // Chỉ tự động hiển thị khi ở trang chủ "/" và chưa xem tour
  // Nhưng không tự động hiển thị nếu đã có popup cảnh báo đang hiển thị (đợi popup cảnh báo đóng trước)
  useEffect(() => {
    const timers = [];
    let removeInteractionListeners = () => {};

    if (location.pathname !== '/') {
      return () => {
        timers.forEach((id) => clearTimeout(id));
      };
    }
    
    // Tạm tắt tự mở tour lần đầu (onboarding_seen_once_v4) — thay bằng video giới thiệu tự chạy ở Home.
    // Tour vẫn mở được thủ công qua sự kiện 'openAppTour' (nút "Xem hướng dẫn").
    const AUTO_TOUR_ENABLED = false;
    if (AUTO_TOUR_ENABLED && !loading && shouldShowOnboardingV2()) {
      const hasCreatedFirstOrder = localStorage.getItem('cask_first_order_created');
      
      if (!hasCreatedFirstOrder) {
        const attemptOpenTour = () => {
          if (typeof window !== 'undefined' && window.__caskActiveAutoPopup) {
            const waitTimer = setTimeout(attemptOpenTour, 500);
            timers.push(waitTimer);
            return;
          }
          onShowTour(true);
        };

        // Chỉ mở sau lần tương tác đầu tiên (chạm/lăn chuột/phím): Chrome chốt LCP ở lần tương tác đầu,
        // nên popup không bị tính là phần tử LCP của trang chủ (trước đây tự mở sau 2 giây làm LCP chậm)
        const onFirstInteraction = () => {
          removeInteractionListeners();
          const timer = setTimeout(attemptOpenTour, 1000);
          timers.push(timer);
        };
        const interactionEvents = ['pointerdown', 'keydown', 'wheel'];
        removeInteractionListeners = () => {
          interactionEvents.forEach((type) => window.removeEventListener(type, onFirstInteraction, true));
        };
        interactionEvents.forEach((type) => window.addEventListener(type, onFirstInteraction, { capture: true, passive: true }));
      }
    }

    return () => {
      removeInteractionListeners();
      timers.forEach((id) => clearTimeout(id));
    };
  }, [loading, location.pathname, onShowTour]);
  
  return null;
};

// This effect commits only after the route inside Suspense is ready.
const InitialRouteReady = ({ onReady }) => {
  useEffect(onReady, [onReady]);
  return null;
};

function App() {
  const { user, loading } = useAuth();
  // Load the route and play the splash animation in parallel.
  const isAuthRoute = window.location.pathname === '/auth/status' || window.location.pathname === '/login';
  const [showAppLoading, setShowAppLoading] = useState(true);
  const [initialRouteReady, setInitialRouteReady] = useState(false);
  const handleInitialRouteReady = React.useCallback(() => setInitialRouteReady(true), []);
  const [showOnboarding, setShowOnboarding] = useState(false);

  // Google Analytics: tải sau khi trang load xong và trình duyệt rảnh, không tranh băng thông/CPU với lần render đầu
  useEffect(() => {
    if (showAppLoading && !isAuthRoute) return undefined;
    let idleId;
    let timer;
    const loadGA = () => {
      const script1 = document.createElement('script');
      script1.async = true;
      script1.defer = true;
      script1.src = 'https://www.googletagmanager.com/gtag/js?id=G-1JPEJH8C53';
      document.head.appendChild(script1);

      const script2 = document.createElement('script');
      script2.innerHTML = `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', 'G-1JPEJH8C53');
      `;
      document.head.appendChild(script2);
    };

    const scheduleGA = () => {
      if ('requestIdleCallback' in window) {
        idleId = window.requestIdleCallback(loadGA, { timeout: 4000 });
      } else {
        timer = setTimeout(loadGA, 1500);
      }
    };

    if (document.readyState === 'complete') {
      scheduleGA();
    } else {
      window.addEventListener('load', scheduleGA, { once: true });
    }

    return () => {
      window.removeEventListener('load', scheduleGA);
      if (idleId && 'cancelIdleCallback' in window) window.cancelIdleCallback(idleId);
      clearTimeout(timer);
    };
  }, [showAppLoading, isAuthRoute]);

  // Tải trước chunk các trang bottom nav lúc rảnh (sau lần render đầu) để bấm tab là chuyển trang ngay
  useEffect(() => {
    if (showAppLoading && !isAuthRoute) return undefined;
    let idleId;
    let timer;
    if ('requestIdleCallback' in window) {
      idleId = window.requestIdleCallback(preloadAllTabPages, { timeout: 3000 });
    } else {
      timer = setTimeout(preloadAllTabPages, 1500);
    }
    return () => {
      if (idleId && 'cancelIdleCallback' in window) window.cancelIdleCallback(idleId);
      clearTimeout(timer);
    };
  }, [showAppLoading, isAuthRoute]);

  // Hiệu ứng xuất hiện khi cuộn cho các trang chính
  useEffect(() => {
    initAOS();
  }, []);

  // Listen for manual tour open request (AppTour v2)
  useEffect(() => {
    const handleOpenTour = () => {
      try {
        localStorage.removeItem('onboarding_seen_once_v4');
      } catch (_) {}
      setShowOnboarding(true);
    };
    window.addEventListener('openAppTour', handleOpenTour);
    window.debugAppTour = () => {
      try {
        localStorage.removeItem('onboarding_seen_once_v4');
      } catch (_) {}
      setShowOnboarding(true);
    };
    return () => {
      window.removeEventListener('openAppTour', handleOpenTour);
      delete window.debugAppTour;
    };
  }, []);

  const closeOnboarding = () => {
    setShowOnboarding(false);
  };

  // Track route changes for GA if available
  useEffect(() => {
    const onRouteChange = () => {
      try {
        if (window.gtag) {
          window.gtag('event', 'page_view', {
            page_path: window.location.pathname + window.location.search,
            page_title: document.title
          });
        }
      } catch (_) {}
    };
    window.addEventListener('popstate', onRouteChange);
    return () => window.removeEventListener('popstate', onRouteChange);
  }, []);
  
  // Webview detection disabled

  // AppLoading owns its exit timing so the fade cannot be cut short.
  const handleLoadingComplete = React.useCallback(() => {
    setShowAppLoading(false);
  }, []);

  const appTree = (
    <NotificationProvider>
      {/* v7_startTransition: chuyển trang trong startTransition, trang chưa tải chunk thì giữ trang cũ trên màn hình
          thay vì thay cả app (kể cả TopBar/BottomNav) bằng fallback "Đang tải trang..." rồi mới hiện trang mới */}
      <HistoryRouter history={appHistory} future={{ v7_startTransition: true }}>
        <RouteCommitSignal />
        <TourHandler loading={loading || (showAppLoading && !isAuthRoute)} onShowTour={setShowOnboarding} />
        <div className="App">
          <Suspense fallback={null}>
            <GlobalNotification />
          </Suspense>
          <Suspense fallback={<ComponentLoading text="Đang tải trang..." />}> 
          <Routes>
          {/* Root: luôn render Home để xử lý token từ URL params */}
          <Route path="/" element={
            <AppContent showBottomNav={true}>
              <FastPageTransition>
                <Home />
              </FastPageTransition>
            </AppContent>
          } />

          {/* Orders */}
          <Route path="/orders" element={
            <OrdersWrapper>
              <AppContent showBottomNav={true}>
                <FastPageTransition>
                  <Orders />
                </FastPageTransition>
              </AppContent>
            </OrdersWrapper>
          } />
          <Route path="/shopeefood" element={
            <AppContent showBottomNav={true}>
              <FastPageTransition>
                <ShopeeFood />
              </FastPageTransition>
            </AppContent>
          } />
          <Route path="/payments" element={user ? 
            <AppContent showBottomNav={true}>
              <FastPageTransition>
                <Payments />
              </FastPageTransition>
            </AppContent> : 
            <Navigate to="/login" replace />
          } />
          <Route path="/payment-setup" element={user ? 
            <AppContent showBottomNav={true}>
              <FastPageTransition>
                <PaymentSetup />
              </FastPageTransition>
            </AppContent> : 
            <Navigate to="/login" replace />
          } />
          <Route path="/account" element={user ? 
            <AppContent showBottomNav={true}>
              <FastPageTransition>
                <Account />
              </FastPageTransition>
            </AppContent> : 
            <Navigate to="/login" replace />
          } />

          {/* Vouchers - public */}
          <Route path="/vouchers" element={
            <AppContent showBottomNav={true}>
              <FastPageTransition>
                <VouchersPage />
              </FastPageTransition>
            </AppContent>
          } />

          {/* Mã giảm giá 0đ Sale 8-8 - public */}
          <Route path="/ma-giam-gia-8-8" element={
            <AppContent showBottomNav={true}>
              <FastPageTransition>
                <VouchersSale88Page />
              </FastPageTransition>
            </AppContent>
          } />
          <Route path="/vouchers/sale-8-8" element={
            <AppContent showBottomNav={true}>
              <FastPageTransition>
                <VouchersSale88Page />
              </FastPageTransition>
            </AppContent>
          } />

          {/* Mã Shop 0đ Sale 15-8 - public */}
          <Route path="/ma-giam-gia-15-8" element={
            <AppContent showBottomNav={true}>
              <FastPageTransition>
                <VouchersSale158Page />
              </FastPageTransition>
            </AppContent>
          } />
          <Route path="/vouchers/sale-15-8" element={
            <AppContent showBottomNav={true}>
              <FastPageTransition>
                <VouchersSale158Page />
              </FastPageTransition>
            </AppContent>
          } />

          {/* Mã Shop 0đ Sale 10-10 - public */}
          <Route path="/ma-giam-gia-10-10" element={
            <AppContent showBottomNav={true}>
              <FastPageTransition>
                <VouchersSale1010Page />
              </FastPageTransition>
            </AppContent>
          } />
          <Route path="/vouchers/sale-10-10" element={
            <AppContent showBottomNav={true}>
              <FastPageTransition>
                <VouchersSale1010Page />
              </FastPageTransition>
            </AppContent>
          } />

          {/* Mã Shop 0đ Sale 15-10 - public */}
          <Route path="/ma-giam-gia-15-10" element={
            <AppContent showBottomNav={true}>
              <FastPageTransition>
                <VouchersSale1510Page />
              </FastPageTransition>
            </AppContent>
          } />
          <Route path="/vouchers/sale-15-10" element={
            <AppContent showBottomNav={true}>
              <FastPageTransition>
                <VouchersSale1510Page />
              </FastPageTransition>
            </AppContent>
          } />

          {/* Mã Shop 0đ Sale 25-10 - public */}
          <Route path="/ma-giam-gia-25-10" element={
            <AppContent showBottomNav={true}>
              <FastPageTransition>
                <VouchersSale2510Page />
              </FastPageTransition>
            </AppContent>
          } />
          <Route path="/vouchers/sale-25-10" element={
            <AppContent showBottomNav={true}>
              <FastPageTransition>
                <VouchersSale2510Page />
              </FastPageTransition>
            </AppContent>
          } />

          {/* Mã Shop 0đ Sale 11-11 - public */}
          <Route path="/ma-giam-gia-11-11" element={
            <AppContent showBottomNav={true}>
              <FastPageTransition>
                <VouchersSale1111Page />
              </FastPageTransition>
            </AppContent>
          } />
          <Route path="/vouchers/sale-11-11" element={
            <AppContent showBottomNav={true}>
              <FastPageTransition>
                <VouchersSale1111Page />
              </FastPageTransition>
            </AppContent>
          } />

          {/* Mã Shop 0đ Sale 12-12 - public */}
          <Route path="/ma-giam-gia-12-12" element={
            <AppContent showBottomNav={true}>
              <FastPageTransition>
                <VouchersSale1212Page />
              </FastPageTransition>
            </AppContent>
          } />
          <Route path="/vouchers/sale-12-12" element={
            <AppContent showBottomNav={true}>
              <FastPageTransition>
                <VouchersSale1212Page />
              </FastPageTransition>
            </AppContent>
          } />

          {/* Flash Sale - public */}
          <Route path="/flashsale" element={
            <AppContent showBottomNav={true}>
              <FastPageTransition>
                <FlashSalePage />
              </FastPageTransition>
            </AppContent>
          } />

          {/* Mã giảm giá - trang đích SEO, dùng chung dữ liệu với khối cuối trang chủ */}
          <Route path="/ma-giam-gia" element={
            <AppContent showBottomNav={true}>
              <FastPageTransition>
                <CouponsPage />
              </FastPageTransition>
            </AppContent>
          } />

          {/* Mã shop 0đ - trang đích SEO, gom các trang mã shop 0đ theo ngày sale */}
          <Route path="/ma-shop-0d" element={
            <AppContent showBottomNav={true}>
              <FastPageTransition>
                <ShopVoucher0dPage />
              </FastPageTransition>
            </AppContent>
          } />

          {/* About - public */}
          <Route path="/gioi-thieu" element={
            <AppContent showBottomNav={false}>
              <FastPageTransition>
                <About />
              </FastPageTransition>
            </AppContent>
          } />

          {/* Lost Order Search */}
          <Route path="/lost-order-search" element={user ? 
            <AppContent showBottomNav={true}>
              <FastPageTransition>
                <LostOrderSearchPage />
              </FastPageTransition>
            </AppContent> : 
            <Navigate to="/login" replace />
          } />


          {/* Hỏi đáp: danh sách + bài hỏi đáp /hoi-dap/<slug>. Bài hướng dẫn tĩnh /hoi-dap/*.html do Cloudflare phục vụ trực tiếp */}
          <Route path="/hoi-dap" element={
            <AppContent showBottomNav={true}>
              <FastPageTransition>
                <QA />
              </FastPageTransition>
            </AppContent>
          } />
          <Route path="/hoi-dap/:slug" element={
            <AppContent showBottomNav={true}>
              <FastPageTransition>
                <QADetail />
              </FastPageTransition>
            </AppContent>
          } />
          {/* URL cũ: /qa, /qa/posts/:id chuyển sang /hoi-dap. Trang sửa bài và quản lý vẫn ở /qa */}
          <Route path="/qa" element={<Navigate to="/hoi-dap" replace />} />
          <Route path="/qa/posts/:id" element={<LegacyQaPostRedirect />} />
          <Route path="/qa/posts/:id/edit" element={user ? 
            <AppContent showBottomNav={true}>
              <FastPageTransition>
                <EditPost />
              </FastPageTransition>
            </AppContent> : 
            <Navigate to="/login" replace />
          } />
          <Route path="/qa/admin" element={
            <AppContent showBottomNav={true}>
              <FastPageTransition>
                <AdminPanel />
              </FastPageTransition>
            </AppContent>
          } />
          <Route path="/admin/banners" element={user ? (
            <AppContent showBottomNav={false}>
              <FastPageTransition>
                <BannersAdmin />
              </FastPageTransition>
            </AppContent>
          ) : (
            <Navigate to="/login" replace />
          )} />

          {/* Auth routes - không hiển thị BottomNavigation */}
          <Route path="/login" element={user ? 
            <Navigate to="/" replace /> : 
            <AppContent showBottomNav={false}>
              <FastPageTransition>
                <Login />
              </FastPageTransition>
            </AppContent>
          } />
          {/* Explicit route to start OAuth from frontend */}
          <Route path="/auth/google" element={
            <PageTransition>
              <GoogleRedirect />
            </PageTransition>
          } />
          {/* Google OAuth callback - handle token then redirect to home */}
          <Route path="/auth/google/callback" element={
            <PageTransition>
              <GoogleCallback />
            </PageTransition>
          } />
          {/* Facebook OAuth routes */}
          <Route path="/auth/facebook" element={
            <PageTransition>
              <FacebookRedirect />
            </PageTransition>
          } />
          <Route path="/auth/facebook/callback" element={
            <PageTransition>
              <FacebookCallback />
            </PageTransition>
          } />
          {/* Zalo OAuth routes đã tắt */}
          <Route path="/auth/status" element={
            <PageTransition>
              <AuthStatus />
            </PageTransition>
          } />

          {/* Fallback - 404 Not Found */}
          <Route path="*" element={
            <AppContent showBottomNav={true}>
              <FastPageTransition>
                <NotFound />
              </FastPageTransition>
            </AppContent>
          } />
          </Routes>
          <InitialRouteReady onReady={handleInitialRouteReady} />
          </Suspense>
        </div>
        
        {/* App Tour V2 - Banner chào mừng + Hướng dẫn từ A đến Z (AppTour cũ tạm tắt) */}
        {showOnboarding && typeof document !== 'undefined' && createPortal(
          <Suspense fallback={null}>
            <AppTourV2 isOpen={true} onClose={closeOnboarding} />
          </Suspense>,
          document.body
        )}
        
      </HistoryRouter>
    </NotificationProvider>
  );

  return (
    <>
      {appTree}
      {showAppLoading && !isAuthRoute && <AppLoading ready={initialRouteReady && !loading} onLoadingComplete={handleLoadingComplete} />}
    </>
  );
}

export default App;
